/**
 * Binary min-heap of (id, key) pairs on typed arrays — the priority queue behind every Dijkstra,
 * A* and priority-flood in the generator. Used with lazy deletion: callers push duplicates and
 * skip stale pops (key > best known). Pop order for equal keys depends only on the push sequence,
 * so it is deterministic.
 */
export class MinHeap {
  private ids: Int32Array;
  private keys: Float64Array;
  size = 0;
  /** Key of the element returned by the last pop(). */
  lastKey = 0;

  constructor(capacity = 1024) {
    this.ids = new Int32Array(capacity);
    this.keys = new Float64Array(capacity);
  }

  clear(): void {
    this.size = 0;
  }

  push(id: number, key: number): void {
    if (this.size === this.ids.length) {
      const ids = new Int32Array(this.size * 2);
      const keys = new Float64Array(this.size * 2);
      ids.set(this.ids);
      keys.set(this.keys);
      this.ids = ids;
      this.keys = keys;
    }
    let i = this.size++;
    const ids = this.ids;
    const keys = this.keys;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent] <= key) break;
      ids[i] = ids[parent];
      keys[i] = keys[parent];
      i = parent;
    }
    ids[i] = id;
    keys[i] = key;
  }

  /** Removes and returns the id with the smallest key (its key is stored in `lastKey`). */
  pop(): number {
    const ids = this.ids;
    const keys = this.keys;
    const top = ids[0];
    this.lastKey = keys[0];
    const n = --this.size;
    if (n > 0) {
      const id = ids[n];
      const key = keys[n];
      let i = 0;
      for (;;) {
        let c = 2 * i + 1;
        if (c >= n) break;
        if (c + 1 < n && keys[c + 1] < keys[c]) c++;
        if (keys[c] >= key) break;
        ids[i] = ids[c];
        keys[i] = keys[c];
        i = c;
      }
      ids[i] = id;
      keys[i] = key;
    }
    return top;
  }
}
